import * as React from 'react';
import { connect } from 'react-redux';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import classNames from 'classnames';

import ImageUploader, { MEDIA_TYPES_IMAGES } from 'components/ImageUploader';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import { withModal } from 'components/modal/ModalHOC';
import ActionLink from 'components/buttons/action-link/ActionLink';
import CropImageModal from 'components/crop-image-modal/CropImageModal';
import RealmOutfitImage from '../realm-outfit-image/RealmOutfitImage';

import { danger } from 'redux-store/reducers/common-reducer';
import {
  DIMENSION_RATIO,
  DIMENSION_RATIO_DEVIATION,
  MAX_SIZE,
  MAX_WIDTH,
  MIN_HEIGHT,
  MIN_WIDTH,
  downloadFileIndividually,
  getImageValidationError
} from 'modules/realm-outfits/reducers/realm-outfits';

import { getImageDimension, uploadImage } from 'utils/image';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { insertIf } from 'utils/list';
import { __t, applyTranslations } from 'utils/language';
import { prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';

import { RuntimeConfigState } from 'types';
import { RealmOutfitImage as RealmOutfitImageType } from 'modules/realm-outfits/types/RealmOutfit.type';

import './realm-outfits-images.scss';

applyTranslations(require('./realm-outfits-images.i18n.json'));

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);
const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

type ModeType = 'create' | 'uploading' | 'drop';

type Image = Omit<RealmOutfitImageType, 'sortOrder'>;

type StateProps = {
  minHeight: number,
  minWidth: number,
  maxWidth: number,
  maxRatio: number,
  maxRatioDeviation: number,
  maxSize: number
}

type DispatchProps = {
  dispatchDanger: (msg: string) => void,
  dispatchDownloadFileIndividually: (url: string) => void
}

type OwnProps = {
  canUpload?: boolean,
  canSortImages?: boolean,
  isInModal?: boolean,
  images?: Array<Image>,
  onDelete?: (index: number) => void,
  onUpload: (images: Array<Image>, position: number | null) => void,
  onImageReorder?: (oldIndex: number, newIndex: number) => void,
  onRearrangeOrder?: (canSortImages: boolean) => void
};

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  unUploadImages: Array<File>,
  isSorting: boolean,
  mode: ModeType,
  currentReplaceImageIndex: null | number
}

const SortableImage: any = SortableElement(({ url }: { url: string}) => <RealmOutfitImage url={url} disabled />);

const SortableImages: any = SortableContainer(({ images }: { images: Array<Image> }) => (
  <div className="realm-outfits-images__list">
    {images.map(({ url }, index) => (
      <SortableImage
        key={url}
        index={index}
        url={url}
      />
    ))}
  </div>
));

const DivWithCropModal = withModal(<div />, {
  modalContent: CropImageModal,
  width: 'small',
  autoOpenModal: true,
  containerClass: 'realm-outfits-images__modal-container'
});

class RealmOutfitsImages extends React.PureComponent<Props, State> {
  static defaultProps = {
    onUpload: (images: Array<Image>): Array<Image> => images,
    images: []
  };

  private uploadRef: React.RefObject<HTMLInputElement> = React.createRef();

  state: State = {
    unUploadImages: [],
    mode: 'create',
    isSorting: false,
    currentReplaceImageIndex: null
  };

  handleUploadFiles = async (image: File) => {
    const { currentReplaceImageIndex } = this.state;
    const {
      minHeight,
      minWidth,
      maxWidth,
      maxRatio,
      maxRatioDeviation,
      maxSize,
      onUpload,
      dispatchDanger
    } = this.props;

    const imageProps = await getImageDimension(image);

    const imageValidationError = getImageValidationError(
      imageProps,
      {
        minHeight,
        minWidth,
        maxWidth,
        maxRatio,
        maxRatioDeviation,
        maxSize
      }
    );

    if (imageValidationError) {
      dispatchDanger(__t(`realm_outfit_review.validation.${imageValidationError}`));
      return Promise.resolve(null);
    }

    try {
      this.setState({
        mode: 'uploading'
      });

      const uploadedImage = await uploadImage(image);

      onUpload([{
        url: uploadedImage,
        name: image.name
      }], currentReplaceImageIndex);

      return true;
    } catch (e) {
      dispatchDanger(__t('realm_outfit_review.message_failed'));

      return false;
    } finally {
      this.setState({ mode: 'create' });
    }
  };

  handleDeleteFile = (index: number) => {
    const { onDelete } = this.props;

    if (onDelete) {
      onDelete(index);
    }
  };

  handleDownloadFile = (url: string) => {
    const { dispatchDownloadFileIndividually } = this.props;
    dispatchDownloadFileIndividually(url);
  };

  handleSortStart = () => this.setState({
    isSorting: true
  });

  handleSortImages = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
    const { onImageReorder } = this.props;

    if (onImageReorder) {
      onImageReorder(oldIndex, newIndex);
    }

    this.setState({
      isSorting: false
    });
  };

  renderPlaceholder(enabledSorting?: boolean) {
    const { mode } = this.state;

    return (
      <ImageUploader
        key={`uploader-${mode}`}
        forwardedRef={this.uploadRef}
        multiple
        mediaTypes={MEDIA_TYPES_IMAGES}
        hasIcon
        onUpload={(files) => {
          this.setState({
            unUploadImages: []
          }, () => {
            this.setState({
              unUploadImages: [...files]
            });
          });
        }}
        onCancel={() => {
          this.setState({
            currentReplaceImageIndex: null
          });
        }}
        button={{
          label: __t('realm_outfits_images.upload_btn'),
          type: 'button',
          color: 'secondary',
          disabled: enabledSorting || mode === 'uploading',
          disabledWithSpinner: mode === 'uploading'
        }}
      />
    );
  }

  renderContextMenu = (url: string, index: number) => {
    const { canUpload } = this.props;

    const items = [
      ...insertIf(canUpload, {
        icon: 'trash-custom-fg',
        text: __t('realm_outfits_images.context_menu.delete'),
        onClick: () => this.handleDeleteFile(index)
      }),
      ...insertIf(canUpload, {
        type: 'separator'
      }),
      {
        icon: 'download-custom-fg',
        text: __t('realm_outfits_images.context_menu.download'),
        onClick: () => this.handleDownloadFile(url)
      },
      ...insertIf(canUpload, {
        type: 'separator'
      }),
      ...insertIf(canUpload, {
        icon: 'add-or-delete-custom-fg',
        text: __t('realm_outfits_images.context_menu.replace_content'),
        onClick: () => {
          if (this.uploadRef.current) {
            this.setState({
              currentReplaceImageIndex: index
            });
            this.uploadRef.current.click();
          }
        }
      })
    ];

    return (
      <MoreButtonWithMenu
        width={20}
        contextMenuProps={{
          items,
          closeOnScroll: true
        }}
      />
    );
  };

  renderSortableImages = () => (
    <div className={this.state.isSorting ? 'realm-outfits-images__wrapper-sorting' : 'realm-outfits-images__wrapper'}>
      <SortableImages
        helperClass="realm-outfits-images__sort-helper"
        images={this.props.images || []}
        axis="xy"
        onSortStart={this.handleSortStart}
        onSortEnd={this.handleSortImages}
      />
    </div>
  );

  renderActions = (enabledSorting?: boolean) => {
    const { canUpload, images = [], onRearrangeOrder } = this.props;
    const hasImages = images.length > 1;

    if (!canUpload) {
      return null;
    }

    return (
      <div className="realm-outfits-images__action">
        {this.renderPlaceholder(enabledSorting)}
        {
          onRearrangeOrder && hasImages && (
            <ActionLink
              className="realm-outfits-images__rearrange"
              color="secondary"
              weight="bold"
              onClick={() => onRearrangeOrder(!enabledSorting)}
            >
              {enabledSorting ? __t('realm_outfits_images.done_rearrange') : __t('realm_outfits_images.rearrange_order')}
            </ActionLink>
          )
        }
      </div>
    );
  };

  renderImages() {
    const { images = [], isInModal } = this.props;
    const items = images.map(({ url }, index) => ({ id: index, url }));
    const galleryItems = prepareDeliverableAttachmentsForGallery(items);

    const getImages = (item, index) => (
      <RealmOutfitImage
        imageIndex={index + 1}
        url={item.url}
        name={item.name}
        contextMenu={this.renderContextMenu(item.url, index)}
      />
    );

    return (
      <React.Fragment>
        {
          items.map((item, index) => {
            if (isInModal) {
              return (
                <div
                  key={`outfit-image-${item.id}`}
                >
                  {getImages(item, index)}
                </div>
              );
            }
            return (
              <DivWithModal
                key={`outfit-image-${item.id}`}
                modalContentProps={{
                  items: galleryItems,
                  currentPosition: index
                }}
              >
                {getImages(item, index)}
              </DivWithModal>
            );
          })
        }
      </React.Fragment>
    );
  }

  render() {
    const { canUpload, canSortImages, minWidth, minHeight, images = [] } = this.props;
    const { unUploadImages } = this.state;

    const hasImages = images.length > 0;
    const enabledSorting = canSortImages && hasImages;
    const classes = classNames('realm-outfits-images__list', {
      'realm-outfits-images__list--empty': !hasImages || !canUpload
    });

    return (
      <div>
        {this.renderActions(enabledSorting)}
        {
          enabledSorting ? this.renderSortableImages() : (
            <div className={classes}>
              {hasImages && this.renderImages()}
              {!hasImages && !canUpload && '-'}
            </div>
          )
        }
        {
          unUploadImages.length > 0 && (
            <DivWithCropModal
              promptdisabled
              modalContentProps={{
                images: unUploadImages,
                minWidth,
                minHeight,
                onUploadFiles: this.handleUploadFiles
              }}
            />
          )
        }
      </div>
    );
  }
}

const mapStateToProps = ({ runtimeConfig }: { runtimeConfig: RuntimeConfigState }): StateProps => ({
  maxRatio: runtimeConfig?.config?.realmOutfitsOutfitImageRatio ?? DIMENSION_RATIO,
  maxRatioDeviation: runtimeConfig?.config?.realmOutfitsOutfitImageRatioDeviation
    ?? DIMENSION_RATIO_DEVIATION,
  maxWidth: runtimeConfig?.config?.realmOutfitsMaxOutfitImageWidth ?? MAX_WIDTH,
  minHeight: runtimeConfig?.config?.realmOutfitsMinOutfitImageHeight ?? MIN_HEIGHT,
  minWidth: runtimeConfig?.config?.realmOutfitsMinOutfitImageWidth ?? MIN_WIDTH,
  maxSize: runtimeConfig?.config?.realmOutfitsMaxOutfitImageSize ?? MAX_SIZE
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDanger: (msg: string) => dispatch(danger(msg)),
  dispatchDownloadFileIndividually: (url: string) => dispatch(downloadFileIndividually(url))
});

export default connect(mapStateToProps, mapDispatchToProps)(RealmOutfitsImages);
